import earcut from 'earcut';
import { latLngToLocalMeters } from '@roadworld/map-generator';
import type { ChunkPayload, OsmBuilding } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';

export interface BuildingMeshResult {
  /** Wall geometry — vertex-colored with the per-building wall tint, UVs mapped for a
   *  tiling facade (windows/door) texture. Doubles as a trimesh collider source. */
  walls: MeshGeometry;
  /** Roof geometry — pitched (hip) for small footprints, flat for large/complex ones.
   *  Vertex-colored with the per-building roof tint. Also a trimesh collider source. */
  roofs: MeshGeometry;
}

export type BuildingLodLevel = 'high' | 'low';

export interface BuildingMeshOptions {
  /** 'high' (default) builds the full mesh with wall skirts and triangulated inner rings.
   *  'low' drops inner rings (no holes / no inner walls) and the skirt — outer walls run
   *  from groundY to topY. Used for cached-ring chunks where the player can barely see
   *  the detail anyway. */
  lodLevel?: BuildingLodLevel;
}

const MIN_BUILDING_HEIGHT = 4;
const MAX_BUILDING_HEIGHT = 18;
/** Walls extend this far below the centroid-sampled ground baseline. The hidden section is
 *  buried inside the heightfield on flat or uphill ground (invisible — the terrain occludes
 *  it). On downhill ground, where the natural floor would otherwise float above terrain, the
 *  skirt becomes the visible part that fills the gap. Sized to cover any plausible slope
 *  drop across a typical building footprint. */
const WALL_SKIRT_DEPTH = 15;
/** Horizontal facade tile width (m). The facade texture (door + windows) repeats every
 *  TILE_WIDTH meters along a wall, so windows land at a sensible spacing. */
const FACADE_TILE_WIDTH = 4;
/** Nominal storey height (m). Walls are subdivided into this many floors so the facade tiles
 *  vertically at a constant scale — the door stays ground-floor-sized and window rows repeat
 *  up a tall building instead of the whole facade stretching. */
const FLOOR_HEIGHT = 3.2;
/** Pitched roofs are only generated for footprints at or below this area (m²) with no inner
 *  rings. Larger/complex buildings keep a flat (but still colored) roof — a pyramid to the
 *  centroid would look like a circus tent on big or L-shaped footprints. */
const PITCHED_ROOF_MAX_AREA = 280;

/** Cartoon wall tints (warm plaster / white / brick / sage). Multiplied by the facade
 *  texture, so windows stay dark while the wall takes the tint. */
const WALL_COLORS: ReadonlyArray<readonly [number, number, number]> = [
  [0.88, 0.84, 0.76],
  [0.93, 0.91, 0.86],
  [0.82, 0.70, 0.58],
  [0.78, 0.58, 0.50],
  [0.74, 0.78, 0.74],
  [0.86, 0.80, 0.70],
];
/** Cartoon roof tints (terracotta / slate / moss / brown tile / dark red). */
const ROOF_COLORS: ReadonlyArray<readonly [number, number, number]> = [
  [0.64, 0.30, 0.22],
  [0.40, 0.42, 0.47],
  [0.32, 0.42, 0.30],
  [0.52, 0.34, 0.24],
  [0.46, 0.26, 0.28],
  [0.30, 0.34, 0.40],
];

interface Accum {
  positions: number[];
  indices: number[];
  normals: number[];
  uvs: number[];
  colors: number[];
}

function newAccum(): Accum {
  return { positions: [], indices: [], normals: [], uvs: [], colors: [] };
}

function toGeometry(a: Accum): MeshGeometry {
  return {
    positions: new Float32Array(a.positions),
    indices: new Uint32Array(a.indices),
    normals: new Float32Array(a.normals),
    uvs: new Float32Array(a.uvs),
    colors: new Float32Array(a.colors),
  };
}

export function buildBuildingMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  options: BuildingMeshOptions = {},
): BuildingMeshResult {
  const walls = newAccum();
  const roofs = newAccum();
  const lodLevel = options.lodLevel ?? 'high';

  for (const b of payload.osm.buildings) {
    appendBuilding(b, payload, heightfield, walls, roofs, lodLevel);
  }

  return { walls: toGeometry(walls), roofs: toGeometry(roofs) };
}

function appendBuilding(
  b: OsmBuilding,
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  walls: Accum,
  roofs: Accum,
  lodLevel: BuildingLodLevel,
): void {
  // Project outer ring to chunk-local meters and skip duplicate closing node.
  const outerRing = projectRing(b.outer, payload);
  if (outerRing.length < 3) return;

  // Determine ground baseline at footprint centroid. The roof sits at `groundY + height`,
  // unchanged from the original implementation — so neighboring buildings keep their
  // relative rooflines on slopes. The wall bottom drops below `groundY` by WALL_SKIRT_DEPTH
  // so the building still meets the ground on the downhill side.
  const centroid = polygonCentroid(outerRing);
  const groundY = sampleHeightfield(heightfield, centroid.x, centroid.z);
  const wallBottomY = lodLevel === 'low' ? groundY : groundY - WALL_SKIRT_DEPTH;

  // Heights — clamp to plausible range to keep mapping data quirks from spawning towers.
  let height = b.heightMeters ?? randomHeight(b.id);
  height = clamp(height, MIN_BUILDING_HEIGHT, MAX_BUILDING_HEIGHT);
  const topY = groundY + height;

  const wallColor = pickColor(b.id, WALL_COLORS);
  const roofColor = pickColor(b.id ^ 0x5bd1e995, ROOF_COLORS);

  // Inner rings (courtyard holes) — high LOD only.
  const innerRings: Array<Array<{ x: number; z: number }>> = [];
  if (b.inners && lodLevel === 'high') {
    for (const inner of b.inners) {
      const ring = projectRing(inner, payload);
      if (ring.length < 3) continue;
      innerRings.push(ring);
    }
  }

  // -- Roof --
  const area = Math.abs(polygonArea(outerRing));
  const bbox = ringBounds(outerRing);
  const minExtent = Math.min(bbox.maxX - bbox.minX, bbox.maxZ - bbox.minZ);
  const wantPitched = innerRings.length === 0 && area <= PITCHED_ROOF_MAX_AREA && minExtent > 0;
  if (wantPitched) {
    appendPitchedRoof(outerRing, centroid, topY, minExtent, roofColor, roofs);
  } else {
    appendFlatRoof(outerRing, innerRings, topY, roofColor, roofs);
  }

  // -- Walls -- each segment of each ring (outer + inners) is split into one quad per storey.
  // U tiles by segment length / FACADE_TILE_WIDTH. The facade texture has two vertical bands:
  // the ground floor (door) maps to V [0, 0.5], every upper floor maps to V [0.5, 1] (windows),
  // so floors stay a constant height instead of the whole facade stretching on tall buildings.
  const storeys = Math.max(1, Math.round(height / FLOOR_HEIGHT));
  const floorH = height / storeys;
  const rings: Array<Array<{ x: number; z: number }>> = [outerRing, ...innerRings];
  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r]!;
    // Outward normals must not depend on OSM ring winding (which is inconsistent). The edge
    // normal (-dz, dx) points to the left of travel — i.e. to the ring's *interior* when the
    // ring is CCW (signed area > 0). So flip it to face outward: for the outer ring flip when
    // CCW; for inner-ring (courtyard) walls the visible face points into the hole, so the rule
    // inverts. Wrong-facing normals here caused walls to light as if in shadow and made
    // normalBias push the shadow-receiver the wrong way (self-shadow acne).
    const ccw = polygonArea(ring) > 0;
    const flip = r === 0 ? ccw : !ccw;
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i]!;
      const b2 = ring[(i + 1) % ring.length]!;
      const dx = b2.x - a.x;
      const dz = b2.z - a.z;
      const segLen = Math.hypot(dx, dz) || 1;
      let nx = -dz / segLen;
      let nz = dx / segLen;
      if (flip) {
        nx = -nx;
        nz = -nz;
      }
      const uB = segLen / FACADE_TILE_WIDTH;
      for (let f = 0; f < storeys; f++) {
        // Ground floor includes the buried skirt below groundY; its V goes negative there and
        // CLAMP keeps the door base pinned at ground. Upper floors tile the window band.
        const yB = f === 0 ? wallBottomY : groundY + f * floorH;
        const yT = groundY + (f + 1) * floorH;
        const vB = f === 0 ? ((yB - groundY) / floorH) * 0.5 : 0.5;
        const vT = f === 0 ? 0.5 : 1;
        const start = walls.positions.length / 3;
        // Quad: a-bottom, b-bottom, b-top, a-top.
        pushVert(walls, a.x, yB, a.z, nx, 0, nz, 0, vB, wallColor);
        pushVert(walls, b2.x, yB, b2.z, nx, 0, nz, uB, vB, wallColor);
        pushVert(walls, b2.x, yT, b2.z, nx, 0, nz, uB, vT, wallColor);
        pushVert(walls, a.x, yT, a.z, nx, 0, nz, 0, vT, wallColor);
        walls.indices.push(start, start + 1, start + 2, start, start + 2, start + 3);
      }
    }
  }
}

/** Flat roof cap (earcut), supporting inner-ring holes. Used for large/complex footprints. */
function appendFlatRoof(
  outerRing: Array<{ x: number; z: number }>,
  innerRings: Array<Array<{ x: number; z: number }>>,
  topY: number,
  color: readonly [number, number, number],
  roofs: Accum,
): void {
  const flat: number[] = [];
  for (const p of outerRing) flat.push(p.x, p.z);
  const holeIndices: number[] = [];
  for (const ring of innerRings) {
    holeIndices.push(flat.length / 2);
    for (const p of ring) flat.push(p.x, p.z);
  }
  const triangles = earcut(flat, holeIndices, 2);
  if (triangles.length === 0) return;
  const base = roofs.positions.length / 3;
  const totalRingPoints = flat.length / 2;
  for (let i = 0; i < totalRingPoints; i++) {
    pushVert(roofs, flat[i * 2]!, topY, flat[i * 2 + 1]!, 0, 1, 0, 0, 0, color);
  }
  for (let t = 0; t < triangles.length; t += 3) {
    roofs.indices.push(base + triangles[t]!, base + triangles[t + 1]!, base + triangles[t + 2]!);
  }
}

/** Pitched hip roof: a tent from each outer edge up to an apex above the centroid. Robust
 *  for any simple polygon; from outside it reads as a proper sloped roof. */
function appendPitchedRoof(
  outerRing: Array<{ x: number; z: number }>,
  centroid: { x: number; z: number },
  topY: number,
  minExtent: number,
  color: readonly [number, number, number],
  roofs: Accum,
): void {
  const roofHeight = clamp(minExtent * 0.4, 1.5, 4);
  const apexY = topY + roofHeight;
  const ax = centroid.x;
  const az = centroid.z;
  for (let i = 0; i < outerRing.length; i++) {
    const a = outerRing[i]!;
    const b = outerRing[(i + 1) % outerRing.length]!;
    // Triangle (a_eave, b_eave, apex). Compute a normal pointing up-and-out.
    const ux = b.x - a.x;
    const uy = 0;
    const uz = b.z - a.z;
    const vx = ax - a.x;
    const vy = apexY - topY;
    const vz = az - a.z;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len;
    ny /= len;
    nz /= len;
    if (ny < 0) {
      nx = -nx;
      ny = -ny;
      nz = -nz;
    }
    const base = roofs.positions.length / 3;
    pushVert(roofs, a.x, topY, a.z, nx, ny, nz, 0, 0, color);
    pushVert(roofs, b.x, topY, b.z, nx, ny, nz, 1, 0, color);
    pushVert(roofs, ax, apexY, az, nx, ny, nz, 0.5, 1, color);
    roofs.indices.push(base, base + 1, base + 2);
  }
}

function pushVert(
  acc: Accum,
  x: number,
  y: number,
  z: number,
  nx: number,
  ny: number,
  nz: number,
  u: number,
  v: number,
  color: readonly [number, number, number],
): void {
  acc.positions.push(x, y, z);
  acc.normals.push(nx, ny, nz);
  acc.uvs.push(u, v);
  acc.colors.push(color[0], color[1], color[2], 1);
}

function projectRing(nodeIds: number[], payload: ChunkPayload): Array<{ x: number; z: number }> {
  const ring: Array<{ x: number; z: number }> = [];
  // Skip the duplicate closing node — earcut treats rings as open.
  const end = nodeIds.length > 0 && nodeIds[0] === nodeIds[nodeIds.length - 1]
    ? nodeIds.length - 1
    : nodeIds.length;
  for (let i = 0; i < end; i++) {
    const id = nodeIds[i]!;
    const node = payload.osm.nodes[id];
    if (!node) continue;
    const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
    ring.push({ x, z });
  }
  return ring;
}

function polygonCentroid(ring: Array<{ x: number; z: number }>): { x: number; z: number } {
  let sx = 0;
  let sz = 0;
  for (const p of ring) {
    sx += p.x;
    sz += p.z;
  }
  const n = ring.length || 1;
  return { x: sx / n, z: sz / n };
}

function polygonArea(ring: Array<{ x: number; z: number }>): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    sum += a.x * b.z - b.x * a.z;
  }
  return sum * 0.5;
}

function ringBounds(ring: Array<{ x: number; z: number }>): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of ring) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minZ, maxZ };
}

function pickColor(
  id: number,
  palette: ReadonlyArray<readonly [number, number, number]>,
): readonly [number, number, number] {
  const h = (Math.imul(id ^ 0x9e3779b9, 2654435761) >>> 0) % palette.length;
  return palette[h]!;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function randomHeight(seed: number): number {
  // Cheap stable pseudo-random from seed so the same building gets the same height each load.
  const h = Math.sin(seed * 12.9898) * 43758.5453;
  const r = h - Math.floor(h);
  return MIN_BUILDING_HEIGHT + r * (12 - MIN_BUILDING_HEIGHT);
}
